import React from "react";
import { fmtUsd, fmtUsdSigned, fmtPct } from "./format";

/**
 * AutomationMetricsBand — the mockup's four-tile band, with All-time ROI as the
 * hero tile.
 *
 * EVERY FIGURE IS THE ENGINE'S OWN. The band renders normalizeReport() output
 * verbatim (reportModel.js) and nothing else. There is no placeholder dollar
 * figure anywhere in this file and none may be added: a tile with no number
 * shows "—" plus the reason, because a plausible-looking equity or ROI is
 * indistinguishable from a real one and this is a live financial surface.
 *
 * COMPLIANCE: these are the member's own realised account figures, past tense.
 * Nothing here is a projection, a target, or a promise, and the ROI tile is
 * tinted only when there is a real number to tint — an em dash in the up colour
 * reads as a gain.
 *
 * @param {Object} props
 * @param {Object|null} props.report   normalizeReport() result, or null
 * @param {boolean} props.available    is the reporting backend connected at all
 * @param {boolean} props.loading      read in flight
 */

const tone = (n) => (n == null ? null : n > 0 ? "sa-up" : n < 0 ? "sa-down" : null);

const Tile = ({ label, value, sub, hero, valueTone, testId }) => (
  <div className="sa-m" data-testid={testId}>
    <p className="sa-m-k">{label}</p>
    <p className={["sa-m-v", hero && valueTone ? valueTone : ""].join(" ").trim()}>{value}</p>
    {sub && (
      <p className={["sa-m-n", hero && valueTone ? valueTone : ""].join(" ").trim()}>{sub}</p>
    )}
  </div>
);

const AutomationMetricsBand = ({ report, available, loading, engineMode = "connecting" }) => {
  /* One reason, stated once per empty tile, rather than four tiles of bare
     dashes the member has to guess at. "Not connected" and "connected, nothing
     to report yet" are different facts and the member can act on only one. */
  const gap = loading
    ? "Reading…"
    : engineMode === "demo"
    ? "Engine not connected"
    : !available
    ? "Reporting not available"
    : engineMode === "connecting"
    ? "Connecting…"
    : "No report yet";

  const withdrawn = report?.withdrawalsUsd ?? null;
  const equity = report?.equityUsd ?? null;
  const roiPct = report?.simpleRoiPct ?? null;
  const totalPnl = report?.totalPnlUsd ?? null;
  const roiTone = tone(roiPct);

  return (
    <div className="sa-metrics" data-testid="sa-metrics-band">
      <Tile
        label="Deposited"
        testId="sa-metric-deposited"
        value={fmtUsd(report?.netDepositsUsd ?? null)}
        /* NET funding, per the compliance invariant in reportModel.js — the
           gross figure is never substituted for it. */
        sub={report?.netDepositsUsd == null ? gap : "Net of withdrawals"}
      />
      <Tile
        label="Taken out"
        testId="sa-metric-withdrawn"
        value={fmtUsd(withdrawn)}
        sub={withdrawn == null ? gap : "Withdrawn to your wallet"}
      />
      <Tile
        label="Current value"
        testId="sa-metric-value"
        value={fmtUsd(equity)}
        sub={equity == null ? gap : "Live — this figure moves"}
      />
      <Tile
        label="All-time ROI"
        testId="sa-metric-roi"
        hero
        valueTone={roiTone}
        value={fmtPct(roiPct, { signed: true })}
        sub={
          roiPct == null
            ? gap
            : totalPnl == null
            ? "Past result — not a forecast"
            : `${fmtUsdSigned(totalPnl)} · past result, not a forecast`
        }
      />
    </div>
  );
};

export default AutomationMetricsBand;
